/**
 * DevTab Action Logger
 * Logs all Fix QBO actions to localStorage for debugging and audit purposes
 */

const LOG_KEY = 'devtab_action_log';

export interface LogEntry {
  timestamp: string;
  action: 'fix_qbo' | 'fix_qbo_associated' | 'sync_customer' | 'sync_invoice' | 'sync_payment' | 'sync_tax_group' | 'lookup' | 'crawl';
  type: 'customer' | 'invoice' | 'payment' | 'tax_group';
  id: string;
  success: boolean;
  message?: string;
  qboId?: string;
  error?: string;
  matches?: number;
  mismatches?: number;
  totalChecked?: number;
  details?: string;
}

/**
 * Add a log entry
 */
export const addLogEntry = (entry: Omit<LogEntry, 'timestamp'>): void => {
  try {
    const logs = getLogEntries();
    const newEntry: LogEntry = {
      ...entry,
      timestamp: new Date().toISOString()
    };
    logs.push(newEntry);
    localStorage.setItem(LOG_KEY, JSON.stringify(logs));
    console.log('📝 DevTab Log Entry Added:', newEntry);
    
    // Dispatch custom event to notify listeners that log entries changed
    window.dispatchEvent(new CustomEvent('devtab-log-updated', { 
      detail: { count: logs.length } 
    }));
  } catch (error) {
    console.error('Failed to add log entry:', error);
  }
};

/**
 * Get all log entries
 */
export const getLogEntries = (): LogEntry[] => {
  try {
    const stored = localStorage.getItem(LOG_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to get log entries:', error);
    return [];
  }
};

/**
 * Clear all logs
 */
export const clearLogs = (): void => {
  try {
    localStorage.removeItem(LOG_KEY);
    console.log('🗑️ DevTab logs cleared');
    
    // Dispatch custom event to notify listeners that log entries changed
    window.dispatchEvent(new CustomEvent('devtab-log-updated', { 
      detail: { count: 0 } 
    }));
  } catch (error) {
    console.error('Failed to clear logs:', error);
  }
};

/**
 * Export logs as formatted text
 */
export const exportLogsAsText = (): string => {
  const logs = getLogEntries();
  if (logs.length === 0) {
    return 'No DevTab actions logged yet.';
  }
  
  let text = '='.repeat(80) + '\n';
  text += 'DevTab Action Log\n';
  text += `Generated: ${new Date().toISOString()}\n`;
  text += `Total Entries: ${logs.length}\n`;
  text += '='.repeat(80) + '\n\n';
  
  logs.forEach((entry, index) => {
    text += `[${index + 1}] ${entry.timestamp}\n`;
    text += `    Action: ${entry.action.toUpperCase()}\n`;
    text += `    Type: ${entry.type}\n`;
    text += `    ID: ${entry.id}\n`;
    text += `    Success: ${entry.success ? '✓ YES' : '✗ NO'}\n`;
    if (entry.qboId) {
      text += `    QBO ID: ${entry.qboId}\n`;
    }
    if (entry.matches !== undefined && entry.totalChecked !== undefined) {
      text += `    Matches: ${entry.matches}/${entry.totalChecked}\n`;
    }
    if (entry.mismatches !== undefined && entry.totalChecked !== undefined) {
      text += `    Mismatches: ${entry.mismatches}/${entry.totalChecked}\n`;
    }
    if (entry.message) {
      text += `    Message: ${entry.message}\n`;
    }
    if (entry.details) {
      text += `    Details:\n${entry.details.split('\n').map(line => `      ${line}`).join('\n')}\n`;
    }
    if (entry.error) {
      text += `    Error: ${entry.error}\n`;
    }
    text += '\n';
  });
  
  return text;
};

/**
 * Download logs as text file
 */
export const downloadLogs = (): void => {
  const text = exportLogsAsText();
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `devtab-action-log-${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  console.log('📥 DevTab logs downloaded');
};

/**
 * Get log statistics
 */
export const getLogStats = (): {
  total: number;
  successful: number;
  failed: number;
  byType: { [key: string]: number };
} => {
  const logs = getLogEntries();
  const stats = {
    total: logs.length,
    successful: logs.filter(l => l.success).length,
    failed: logs.filter(l => !l.success).length,
    byType: {} as { [key: string]: number }
  };
  
  logs.forEach(log => {
    stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;
  });
  
  return stats;
};

